import * as Sentry from '@sentry/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { addCustomerAddressAPI, deleteCustomerAddressAPI, editCustomerAddressAPI, fetchCustomerAddressesAPI, validateAddressAPI } from 'api/address';
import type { Addresses } from 'api/types/addresses.types';
import { useConfig } from 'context/app-config';
import { selectCurrentOrder } from 'pages/newOrder/newOrder.selectors';
import { selectAddress } from 'pages/newOrder/newOrder.slice';
import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectAuthToken, selectCustomer, selectIsFirstTimeCustomer } from 'redux-stores/slices/authSlice';
import type { AppDispatch } from 'redux-stores/store';
import type { AddressForm } from 'schemas/address-schema';

import { REACT_QUERY_KEYS } from '.';

export const useDeleteCustomerAddress = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: [REACT_QUERY_KEYS.addressKeys.DELETE_CUSTOMER_ADDRESS],
    mutationFn: ({ id }: { id: string }) => deleteCustomerAddressAPI(id),
    onSuccess: (data) => {
      queryClient.invalidateQueries([REACT_QUERY_KEYS.addressKeys.GET_ALL_CUSTOMER_ADDRESSES], {
        refetchType: 'all',
      });
      queryClient.setQueryData([REACT_QUERY_KEYS.userKeys.FETCH_CUSTOMER], data.updatedUser);
    },
  });
};

export const useGetAllCustomerAddresses = ({ enabled = true }: { enabled?: boolean }) => {
  const dispatch = useDispatch<AppDispatch>();
  const { addressId } = useSelector(selectCurrentOrder);
  const config = useConfig();
  const isFirstTime = useSelector(selectIsFirstTimeCustomer);
  const customer = useSelector(selectCustomer);
  const authToken = useSelector(selectAuthToken);

  Sentry.addBreadcrumb({
    category: 'get-customer-addresses',
    message: 'useGetAllCustomerAddresses',
    data: {
      isEnabled: enabled,
      hasToken: Boolean(authToken),
      hasCustomerId: Boolean(customer?.id),
    },
  });

  return useQuery({
    queryKey: [REACT_QUERY_KEYS.addressKeys.GET_ALL_CUSTOMER_ADDRESSES],
    queryFn: () => fetchCustomerAddressesAPI(config?.showLastUsedAddressOnly),
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    select: useCallback(
      (data: { addresses: Addresses.Address[] }) => {
        // addresses are available, but no address is selected and not even a first time user.
        // handle a case when user change serviceLines selections and addressId resets.
        if (!addressId && data?.addresses && data?.addresses?.length > 0 && !isFirstTime) {
          const preSelectedAddress = data.addresses.find((addr) => addr.id === customer?.lastUsedAddressId) ?? data.addresses[0];
          dispatch(selectAddress(preSelectedAddress.id));
        }

        // if we have a already address, mark it as inuse
        return data?.addresses.map((address: Addresses.Address) => ({
          ...address,
          isInUse: address.id === addressId,
        }));
      },
      [addressId, dispatch, isFirstTime]
    ),
    onSuccess: (addresses) => {
      if (!addressId && addresses && addresses.length > 0 && !isFirstTime) {
        const preSelectedAddress = addresses.find((addr) => addr.id === customer?.lastUsedAddressId) ?? addresses[0];
        dispatch(selectAddress(preSelectedAddress.id));
      }
    },
    enabled: enabled && Boolean(authToken),
  });
};

export const useAddCustomerAddress = ({
  onSuccess,
  onError,
}: {
  onSuccess?: (data: { address: Addresses.Address }) => void;
  onError?: (error: unknown) => void;
}) => {
  const config = useConfig();
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: [REACT_QUERY_KEYS.addressKeys.ADD_CUSTOMER_ADDRESS],
    mutationFn: (address: AddressForm) => (config?.api?.addAddress ? config?.api?.addAddress(address) : addCustomerAddressAPI(address)),
    onSuccess: (data: { address: Addresses.Address }) => {
      queryClient.invalidateQueries([REACT_QUERY_KEYS.addressKeys.GET_ALL_CUSTOMER_ADDRESSES], {
        refetchType: 'all',
      });
      onSuccess?.(data);
    },
    onError,
  });
};

export const useEditCustomerAddress = ({
  onSuccess,
  onError,
}: {
  onSuccess?: (address: Addresses.Address) => void;
  onError?: (error: unknown) => void;
}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: [REACT_QUERY_KEYS.addressKeys.EDIT_CUSTOMER_ADDRESS],
    mutationFn: ({ address, addressId }: { address: AddressForm; addressId: string }) => editCustomerAddressAPI(address, addressId),
    onSuccess: (data) => {
      queryClient.invalidateQueries([REACT_QUERY_KEYS.addressKeys.GET_ALL_CUSTOMER_ADDRESSES], {
        refetchType: 'all',
      });
      onSuccess?.(data);
    },
    onError,
  });
};

export const useCheckCoverageAddress = ({
  onSuccess,
  onError,
}: {
  onSuccess?: (data: { geofence: Addresses.Geofence }) => void;
  onError?: (error: unknown) => void;
}) => {
  const mutation = useMutation({
    mutationKey: [REACT_QUERY_KEYS.addressKeys.CHECK_COVERAGE_ADDRESS],
    mutationFn: (position: { lat: number; lng: number }) => validateAddressAPI(position),
    onSuccess,
    onError,
  });

  return mutation;
};
